import React, { useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ExpandMore, FilterList, FilterNone, Refresh, Search } from '@material-ui/icons';
import moment from 'moment';
import { UserDetails } from '../../types';
import { ActivityEventDictionary, ActivityEventGroups, ActivityLog, ActivityQuery } from './types';
import { eventCategory, eventLabel, indexEvents, isEventGroups } from './events';
import { useActivityLogs } from './useActivityLogs';
import ActivityEventFilterDialog from './ActivityEventFilterDialog';
import ActivityScanStatus from './ActivityScanStatus';

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3, 4, 6), color: '#17171F' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: 20,
  },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  muted: { color: '#5C5C70', fontSize: 13 },
  date: { fontSize: 14 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '12px 16px',
    border: '1px solid #E6E6EE',
    borderRadius: 12,
    marginBottom: 20,
  },
  filterDivider: { width: 1, height: 24, background: '#E6E6EE', flexShrink: 0 },
  filterText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    maxWidth: '100%',
  },
  filterName: { color: '#5C5C70', fontSize: 13, fontWeight: 400 },
  filterValue: {
    color: '#17171F',
    fontSize: 13,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filterCount: {
    marginLeft: 8,
    padding: '1px 8px',
    borderRadius: 12,
    fontSize: 12,
    background: 'rgba(255,255,255,0.22)',
  },
  pill: {
    borderRadius: 24,
    textTransform: 'none',
    borderColor: '#E6E6EE',
    whiteSpace: 'nowrap',
    fontWeight: 700,
  },
  eventChip: {
    background: '#F0E9FF',
    border: '1px solid #DCCFFB',
    maxWidth: 320,
    height: 36,
    '& .MuiChip-label': { display: 'flex', minWidth: 0 },
  },
  search: {
    marginLeft: 'auto',
    width: 335,
    maxWidth: '100%',
    height: 40,
    padding: '0 16px',
    border: '1px solid #E6E6EE',
    borderRadius: 24,
    background: '#F4F4F8',
    fontSize: 14,
    '&:focus-within': { borderColor: theme.palette.primary.main },
    '& input': { padding: 0, border: 0, outline: 0, background: 'transparent' },
  },
  searchIcon: { fontSize: 18, color: '#5C5C70', marginRight: 10 },
  tableCard: { border: '1px solid #E6E6EE', borderRadius: 12, overflow: 'hidden' },
  tableScroll: { overflowX: 'auto' },
  table: {
    minWidth: 950,
    tableLayout: 'fixed',
    '& th, & td': { borderColor: '#EEEEF3' },
    // The shared theme's header padding is !important; override it only in this table.
    '& .MuiTableCell-head': {
      background: '#FAFAFC',
      padding: '12px 20px !important',
      lineHeight: '14px',
    },
    '& td': { padding: '12px 20px', verticalAlign: 'middle' },
  },
  heading: {
    display: 'block',
    color: '#17171F',
    fontSize: 11,
    lineHeight: '14px',
    letterSpacing: '.08em',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  subheading: {
    display: 'block',
    fontSize: 12,
    lineHeight: '16px',
    marginTop: 2,
    color: '#5C5C70',
    fontWeight: 400,
  },
  cell: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  mono: { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere', maxWidth: '100%' },
  description: {
    fontSize: 12.5,
    lineHeight: '18px',
    color: '#5C5C70',
    overflowWrap: 'anywhere',
    maxWidth: '100%',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  messagePreview: {
    maxWidth: '100%',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  eventMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    maxWidth: '100%',
  },
  eventName: { fontSize: 14, color: '#17171F', overflowWrap: 'anywhere', minWidth: 0 },
  badge: { fontSize: 11, fontWeight: 700, height: 24, borderRadius: 12 },
  actor: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  actorName: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 400,
    color: '#17171F',
    overflowWrap: 'anywhere',
  },
  ipAddress: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'monospace',
    fontSize: 12.5,
    color: '#5C5C70',
    overflowWrap: 'anywhere',
    '& svg': { flexShrink: 0 },
  },
  actorBadge: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.04em',
    height: 20,
    borderRadius: 4,
    color: '#5C5C70',
    background: '#EEF1F5',
    '& .MuiChip-label': { padding: '0 7px' },
  },
  logId: { display: 'flex', alignItems: 'center', gap: '6px', maxWidth: '100%' },
  copyIcon: {
    padding: 3,
    flexShrink: 0,
    color: theme.palette.primary.main,
    '& svg': { fontSize: 16 },
  },
  expanded: { background: '#FCFAFF' },
  contextHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  contextMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    fontSize: 13,
    color: '#5C5C70',
  },
  contextTitle: { color: '#17171F', fontWeight: 700 },
  contextGroup: { display: 'inline-flex', alignItems: 'baseline', gap: '8px' },
  raw: {
    color: '#17171F',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    maxHeight: 400,
    overflow: 'auto',
    background: '#F7F5FA',
    borderRadius: 8,
    padding: 16,
  },
  footer: {
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  status: { display: 'flex', alignItems: 'center', gap: '10px' },
  empty: { padding: theme.spacing(7, 3), textAlign: 'center' },
  popover: { padding: 24, display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 260 },
  presets: { display: 'flex', gap: '6px', flexWrap: 'wrap', maxWidth: 340 },
  error: { color: theme.palette.error.main, padding: '12px 20px' },
  size: { width: 105 },
}));

const SEVERITIES = ['ALL', 'HIGH', 'MEDIUM', 'LOW'];
const ACTORS = ['ANYONE', 'ADMIN', 'USER', 'SYSTEM', 'UNKNOWN'];
const titleCase = (value: string) => value.charAt(0) + value.slice(1).toLowerCase();
const dateInput = (value: number) => moment.utc(value).format('YYYY-MM-DDTHH:mm');

const FilterText = ({ name, value }: { name: string; value: string }) => {
  const classes = useStyles();
  return (
    <span className={classes.filterText}>
      <span className={classes.filterName}>{name}</span>
      <span className={classes.filterValue}>{value}</span>
    </span>
  );
};

// The search clock ticks every second; unchanged rows should not reformat their raw context.
const ActivityRow = React.memo(
  ({ log, dictionary }: { log: ActivityLog; dictionary: ActivityEventDictionary }) => {
    const classes = useStyles();
    const notify = useNotify();
    const [expanded, setExpanded] = useState(false);
    const category = eventCategory(log.event, dictionary);
    // Lines logged before SDK 4.1.21 carry the raw "audit_event=..." head as message.
    const message = log.message && !log.message.startsWith('audit_event=') ? log.message : null;
    // Free-text lines have no event, their message is the best title.
    const title = log.event
      ? eventLabel(log.event, dictionary)
      : message || eventLabel(null, dictionary);
    const description = message && message !== title ? message : null;
    const context = JSON.stringify(log.context, null, 2);
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(context);
        notify('Activity log context copied');
      } catch (error) {
        notify('Could not copy the context', 'warning');
      }
    };
    const copyLogId = async () => {
      if (!log.logId) return;
      try {
        const logId = log.logId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        await navigator.clipboard.writeText(`@log_id:"${logId}"`);
        notify('Log ID copied as a Datadog query');
      } catch (error) {
        notify('Could not copy the log ID', 'warning');
      }
    };
    return (
      <>
        <TableRow className={expanded ? classes.expanded : undefined}>
          <TableCell>
            <Box className={classes.cell}>
              <Box className={classes.eventMeta}>
                <Chip
                  size="small"
                  label={category.name}
                  className={classes.badge}
                  style={{ color: category.color, background: category.background }}
                />
                <span
                  className={
                    !log.event && message
                      ? `${classes.eventName} ${classes.messagePreview}`
                      : classes.eventName
                  }
                  title={log.event || log.message}>
                  {title}
                </span>
              </Box>
              {description && (
                <span className={classes.description} title={description}>
                  {description}
                </span>
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Box className={classes.cell}>
              <Box className={classes.actor}>
                <Chip
                  size="small"
                  className={classes.actorBadge}
                  label={log.actorType.toUpperCase()}
                />
                <span className={classes.actorName}>
                  {log.actor || (log.actorType === 'USER' ? 'Account user' : '—')}
                </span>
              </Box>
              <span className={classes.ipAddress}>
                {log.ipAddress && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    aria-hidden="true"
                    focusable="false">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
                  </svg>
                )}
                <span>{log.ipAddress || 'No IP recorded'}</span>
              </span>
            </Box>
          </TableCell>
          <TableCell>
            <Box className={classes.cell}>
              <span className={classes.date}>
                {moment.utc(log.timestamp).format('MMM D, YYYY')}
              </span>
              <span className={classes.muted}>
                {moment.utc(log.timestamp).format('HH:mm:ss.SSS')}
              </span>
            </Box>
          </TableCell>
          <TableCell>
            <Box className={classes.cell}>
              <span className={classes.mono}>{log.service}</span>
              <Box className={classes.logId}>
                <span className={classes.mono}>{log.logId || 'No log ID'}</span>
                {log.logId && (
                  <Tooltip title="Copy as Datadog query (@log_id:…)">
                    <IconButton
                      className={classes.copyIcon}
                      aria-label="Copy log ID as Datadog query"
                      onClick={copyLogId}>
                      <FilterNone />
                    </IconButton>
                  </Tooltip>
                )}
                <Chip
                  size="small"
                  className={classes.badge}
                  label={titleCase(log.severity)}
                  style={{
                    background:
                      log.severity === 'HIGH'
                        ? '#FEECEB'
                        : log.severity === 'MEDIUM'
                          ? '#FFF3DD'
                          : '#EBF1FF',
                  }}
                />
              </Box>
            </Box>
          </TableCell>
          <TableCell align="center">
            <Button
              variant="outlined"
              color="primary"
              className={classes.pill}
              aria-expanded={expanded}
              aria-controls={`context-${log.id}`}
              onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Hide context' : 'Show context'}{' '}
              <ExpandMore style={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
            </Button>
          </TableCell>
        </TableRow>
        {expanded && (
          <TableRow className={classes.expanded}>
            <TableCell colSpan={5}>
              <div id={`context-${log.id}`}>
                {message && (
                  <>
                    <Box className={classes.contextMeta}>
                      <span className={classes.contextTitle}>Message</span>
                    </Box>
                    <pre className={classes.raw} tabIndex={0}>
                      {message}
                    </pre>
                  </>
                )}
                <Box className={classes.contextHeader}>
                  <Box className={classes.contextMeta}>
                    <span className={classes.contextTitle}>Raw context</span>
                    <span aria-hidden="true">·</span>
                    <span>{new Blob([context]).size.toLocaleString()} bytes</span>
                    <span aria-hidden="true">·</span>
                    <span className={classes.contextGroup}>
                      log group <span className={classes.mono}>{log.logGroup}</span>
                    </span>
                  </Box>
                  <Button className={classes.pill} variant="outlined" size="small" onClick={copy}>
                    Copy
                  </Button>
                </Box>
                <pre className={classes.raw}>{context}</pre>
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }
);

const ActivityLogsTab = ({ user }: { user: UserDetails }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [query, setQuery] = useState<ActivityQuery>(() => ({
    tenantId: user.id,
    billingId: user.billingUserId,
    from: Date.now() - 7 * 86400000,
    to: Date.now(),
    severity: 'ALL',
    events: [],
    actor: 'ANYONE',
    service: '',
    q: '',
  }));
  const [pageSize, setPageSize] = useState(20);
  const [relativeDays, setRelativeDays] = useState<number | null>(7);
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState<ActivityEventGroups>({});
  const eventDictionary = useMemo(() => indexEvents(events), [events]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(false);
  const [dictionaryVersion, setDictionaryVersion] = useState(0);
  const [eventDialog, setEventDialog] = useState(false);
  const [menu, setMenu] = useState<{ name: string; anchor: HTMLElement } | null>(null);
  const [dateFrom, setDateFrom] = useState(dateInput(query.from));
  const [dateTo, setDateTo] = useState(dateInput(query.to));
  const [service, setService] = useState('');
  const scan = useActivityLogs(query, pageSize);

  useEffect(() => {
    const controller = new AbortController();
    setEventsLoading(true);
    setEventsError(false);
    dataProvider
      .get('activityLogEvents', { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        if (!isEventGroups(data)) throw new Error('Invalid event dictionary');
        setEvents(data);
        setEventsLoading(false);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setEventsLoading(false);
          setEventsError(true);
        }
      });
    return () => controller.abort();
  }, [dataProvider, dictionaryVersion]);

  const apply = (patch: Partial<ActivityQuery>) => {
    scan.cancel();
    const next = { ...query, ...patch };
    if (JSON.stringify(next) === JSON.stringify(query)) scan.refresh();
    else setQuery(next);
    setMenu(null);
  };
  const refresh = () => {
    const now = Date.now();
    apply({
      q: search.trim(),
      ...(relativeDays ? { from: now - relativeDays * 86400000, to: now } : {}),
    });
  };
  const preset = (days: number) => {
    const now = Date.now();
    setRelativeDays(days);
    apply({ from: now - days * 86400000, to: now });
  };
  const reset = () => {
    setSearch('');
    setService('');
    setRelativeDays(7);
    const now = Date.now();
    apply({
      severity: 'ALL',
      actor: 'ANYONE',
      events: [],
      service: '',
      q: '',
      from: now - 7 * 86400000,
      to: now,
    });
  };
  const applyDates = () => {
    const from = moment.utc(dateFrom).valueOf();
    // CloudWatch's endTime is exclusive; retain milliseconds when using preset ranges.
    const to = moment.utc(dateTo).valueOf();
    if (
      !Number.isFinite(from) ||
      !Number.isFinite(to) ||
      from >= to ||
      to - from > 90 * 86400000 ||
      from < Date.UTC(2024, 0, 1)
    ) {
      notify('Select a valid UTC range of up to 90 days after January 1, 2024', 'warning');
      return;
    }
    setRelativeDays(null);
    apply({ from, to });
  };
  const openMenu = (name: string, anchor: HTMLElement) => {
    setDateFrom(dateInput(query.from));
    setDateTo(dateInput(query.to));
    setService(query.service);
    setMenu({ name, anchor });
  };
  const busy = scan.status === 'loading';
  const draftChanged = query.q !== search.trim();
  const dateLabel = relativeDays
    ? relativeDays === 1
      ? 'Last 24 hours'
      : `Last ${relativeDays} days`
    : 'Custom range';
  const filterCount =
    Number(query.severity !== 'ALL') +
    Number(query.events.length > 0) +
    Number(query.actor !== 'ANYONE') +
    Number(Boolean(query.service)) +
    Number(Boolean(query.q));
  const eventSummary = query.events.length
    ? query.events
        .slice(0, 2)
        .map((event) => eventLabel(event, eventDictionary))
        .join(', ') + (query.events.length > 2 ? ` +${query.events.length - 2}` : '')
    : 'All events';

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography component="h2" className={classes.title}>
            Activity logs
          </Typography>
          <Typography className={classes.muted}>
            {scan.logs.length} events loaded · {moment.utc(query.from).format('MMM D, YYYY HH:mm')}{' '}
            – {moment.utc(query.to).format('MMM D, YYYY HH:mm')} · times in UTC · sourced from
            CloudWatch
          </Typography>
        </Box>
        <Box className={classes.status}>
          {scan.fetchedAt && (
            <span className={classes.muted}>
              Fetched {moment.utc(scan.fetchedAt).format('HH:mm:ss')} UTC
            </span>
          )}
          <Button
            className={classes.pill}
            variant="outlined"
            onClick={refresh}
            startIcon={<Refresh />}>
            Refresh
          </Button>
        </Box>
      </Box>
      <Paper
        elevation={0}
        className={classes.toolbar}
        component="section"
        aria-label="Activity filters">
        <Button
          className={classes.pill}
          variant="contained"
          color="primary"
          startIcon={<FilterList />}
          disabled={eventsLoading}
          onClick={() => (eventsError ? setDictionaryVersion((v) => v + 1) : setEventDialog(true))}>
          {eventsLoading ? (
            'Loading filters…'
          ) : eventsError ? (
            'Retry event filters'
          ) : (
            <>
              <span>Filters</span>
              {filterCount > 0 && <span className={classes.filterCount}>{filterCount}</span>}
            </>
          )}
        </Button>
        <span className={classes.filterDivider} aria-hidden="true" />
        <Chip
          label={<FilterText name="Event" value={eventSummary} />}
          className={classes.eventChip}
          disabled={eventsLoading || eventsError}
          onClick={() => setEventDialog(true)}
          onDelete={query.events.length ? () => apply({ events: [] }) : undefined}
        />
        <Button
          className={classes.pill}
          variant="outlined"
          endIcon={<ExpandMore />}
          onClick={(event) => openMenu('severity', event.currentTarget)}>
          <FilterText name="Severity" value={titleCase(query.severity)} />
        </Button>
        <Button
          className={classes.pill}
          variant="outlined"
          endIcon={<ExpandMore />}
          onClick={(event) => openMenu('date', event.currentTarget)}>
          <FilterText name="Date" value={dateLabel} />
        </Button>
        <Button
          className={classes.pill}
          variant="outlined"
          endIcon={<ExpandMore />}
          onClick={(event) => openMenu('actor', event.currentTarget)}>
          <FilterText name="Actor" value={titleCase(query.actor)} />
        </Button>
        <Button
          className={classes.pill}
          variant="outlined"
          endIcon={<ExpandMore />}
          onClick={(event) => openMenu('service', event.currentTarget)}>
          <FilterText name="Service" value={query.service || 'All services'} />
        </Button>
        <Button color="primary" onClick={reset}>
          Reset
        </Button>
        <InputBase
          className={classes.search}
          type="search"
          startAdornment={<Search className={classes.searchIcon} />}
          placeholder="log_id, context..."
          value={search}
          inputProps={{ maxLength: 500, 'aria-label': 'Search context' }}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') refresh();
          }}
        />
        <Button className={classes.pill} color="primary" onClick={refresh} startIcon={<Search />}>
          Search
        </Button>
      </Paper>
      {draftChanged && (
        <Typography className={classes.muted}>
          Search text changed. Press Search to apply it.
        </Typography>
      )}
      <ActivityScanStatus
        status={scan.status}
        requiresRefresh={scan.requiresRefresh}
        loaded={scan.logs.length}
        target={scan.target}
        elapsedMs={scan.elapsedMs}
        fetchedAt={scan.fetchedAt}
        requestStartedAt={scan.requestStartedAt}
        retryAttempt={scan.retryAttempt}
        stopReason={scan.stopReason}
        progress={scan.progress}
        rangeDays={(query.to - query.from) / 86400000}
        severity={query.severity}
        onCancel={scan.cancel}
        onResume={scan.resume}
        onRefresh={refresh}
      />
      <Paper
        elevation={0}
        className={classes.tableCard}
        component="section"
        aria-label="Activity events">
        {scan.logs.length > 0 && (
          <Box className={classes.tableScroll}>
            <Table className={classes.table} aria-label="Activity log results">
              <TableHead>
                <TableRow>
                  {[
                    ['What', 'category · event · description', '40%'],
                    ['Who', 'actor · IP address', '15%'],
                    ['When', 'UTC', '11%'],
                    ['Where', 'service · log id · severity', '22%'],
                    ['Context', 'raw dump', '12%'],
                  ].map(([heading, detail, width]) => (
                    <TableCell
                      key={heading}
                      style={{ width }}
                      align={heading === 'Context' ? 'center' : 'left'}>
                      <span className={classes.heading}>{heading}</span>
                      <span className={classes.subheading}>{detail}</span>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scan.logs.map((log) => (
                  <ActivityRow key={log.id} log={log} dictionary={eventDictionary} />
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {!scan.logs.length && (
          <Box className={classes.empty}>
            {busy && <CircularProgress size={28} />}
            <Typography>
              {busy
                ? 'Scanning activity logs…'
                : scan.status === 'complete'
                  ? 'No matching activity in this range'
                  : scan.status === 'paused'
                    ? 'Search paused'
                    : 'Search interrupted'}
            </Typography>
            <Typography className={classes.muted}>
              {busy
                ? 'Searching the selected severities. Matches will appear here as they are returned.'
                : scan.status === 'complete'
                  ? 'Try a different date range or filters.'
                  : scan.requiresRefresh
                    ? 'Review the filters and refresh to start a new search.'
                    : 'Resume or refresh to continue searching.'}
            </Typography>
          </Box>
        )}
        {scan.error && (
          <Typography role="alert" className={classes.error}>
            {scan.error}
          </Typography>
        )}
        <Box className={classes.footer}>
          <Box className={classes.status} role="status" aria-live="polite">
            {busy && <CircularProgress size={16} />}
            <span className={classes.muted}>
              {busy
                ? `Loading ${scan.logs.length} / ${scan.target} events…`
                : `Showing ${scan.logs.length} events · newest first · ${scan.status === 'complete' ? 'scan complete' : scan.status === 'full' ? 'page filled' : scan.status === 'paused' ? 'paused' : 'interrupted'}`}
            </span>
          </Box>
          <Box className={classes.status}>
            <TextField
              select
              label="Page size"
              variant="outlined"
              margin="dense"
              className={classes.size}
              value={pageSize}
              onChange={(event) => {
                scan.cancel();
                setPageSize(Number(event.target.value));
              }}>
              {[5, 10, 20, 25, 50, 100].map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            {scan.status === 'full' && (
              <Button className={classes.pill} variant="outlined" onClick={scan.loadMore}>
                Load {pageSize} more
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      {eventDialog && (
        <ActivityEventFilterDialog
          events={events}
          selected={query.events}
          onClose={() => setEventDialog(false)}
          onApply={(selected) => {
            setEventDialog(false);
            apply({ events: selected });
          }}
        />
      )}
      <Menu
        open={menu?.name === 'severity' || menu?.name === 'actor'}
        anchorEl={menu?.anchor}
        onClose={() => setMenu(null)}>
        {(menu?.name === 'severity' ? SEVERITIES : ACTORS).map((value) => (
          <MenuItem
            key={value}
            selected={value === (menu?.name === 'severity' ? query.severity : query.actor)}
            onClick={() =>
              apply(menu?.name === 'severity' ? { severity: value } : { actor: value })
            }>
            {titleCase(value)}
          </MenuItem>
        ))}
      </Menu>
      <Popover
        open={menu?.name === 'date' || menu?.name === 'service'}
        anchorEl={menu?.anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setMenu(null)}>
        <Box className={classes.popover}>
          {menu?.name === 'date' ? (
            <>
              <Typography variant="subtitle2">Date range · UTC</Typography>
              <Box className={classes.presets}>
                {[1, 7, 30, 90].map((days) => (
                  <Button key={days} color="primary" onClick={() => preset(days)}>
                    {days === 1 ? 'Last 24 hours' : `${days} days`}
                  </Button>
                ))}
              </Box>
              <TextField
                type="datetime-local"
                label="From (UTC)"
                value={dateFrom}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setDateFrom(event.target.value)}
              />
              <TextField
                type="datetime-local"
                label="To (UTC, exclusive)"
                value={dateTo}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setDateTo(event.target.value)}
              />
              <Button
                color="primary"
                variant="contained"
                className={classes.pill}
                onClick={applyDates}>
                Apply dates
              </Button>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                label="Service contains"
                helperText="For example: backend-admin, billing, ups"
                value={service}
                inputProps={{ maxLength: 200 }}
                onChange={(event) => setService(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') apply({ service: service.trim() });
                }}
              />
              <Button color="primary" onClick={() => apply({ service: '' })}>
                All services
              </Button>
              <Button
                color="primary"
                variant="contained"
                className={classes.pill}
                onClick={() => apply({ service: service.trim() })}>
                Apply service
              </Button>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default ActivityLogsTab;
